import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import DataTable from '@/components/ui/DataTable/DataTable';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import Button from '@/components/ui/Button';
import { useGetAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';
import { KollelDtoIn, KollelStatus } from '@/models/in/KollelDtoIn';
import { KollelFolderDtoIn, KollelFolderStatus } from '@/models/in/KollelFolderDtoIn';
import { getKollelFolderStatusColor, getKollelFolderStatusLabelKey } from '@/helpers/kollelFolderStatus';
import { formatCurrencyShort } from '@/helpers/currencyHelper';
import NewKollelFolderDialog from './NewKollelFolderDialog';

interface KollelFoldersDialogProps {
  fundId?: number;
  kollel: KollelDtoIn;
  open: boolean;
  onClose: () => void;
}

const folderTotal = (folder: KollelFolderDtoIn) => folder.items.reduce((sum, item) => sum + item.totalAmount, 0);

const folderSearch: FilterFn<KollelFolderDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  return row.original.requestedForMonth.toLowerCase().includes(String(value).toLowerCase());
};

/**
 * A kollel's monthly request folders (HAT-1446 phase 6, "תיקיות בקשות"): one row per calendar
 * month the coordinator opened a consolidated stipend request for. "+ בקשה חדשה" only shows once
 * the kollel itself is welfare-committee Approved - a folder is the kollel asking for money, which
 * only makes sense once the kollel itself is recognised (server enforces this too).
 */
const KollelFoldersDialog = ({ fundId, kollel, open, onClose }: KollelFoldersDialogProps) => {
  const { t } = useTranslation();
  const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);
  const [folderToCancel, setFolderToCancel] = useState<KollelFolderDtoIn | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const foldersUrl = fundId && open ? `funds/${fundId}/kollels/${kollel.id}/folders` : '';
  const { data: folders, isLoading, refetch } = useGetAuthApi<KollelFolderDtoIn[]>(foldersUrl);

  const cancelMutation = usePutAuthApi<void>(
    fundId && folderToCancel ? `funds/${fundId}/kollels/${kollel.id}/folders/${folderToCancel.id}/cancel` : '',
  );

  const handleCancel = () => {
    cancelMutation.mutate(
      {},
      {
        onSuccess: () => {
          setFolderToCancel(null);
          refetch();
        },
      },
    );
  };

  const columns: ColumnDef<KollelFolderDtoIn>[] = [
    {
      id: 'requestedForMonth',
      header: Col(t('kollels.folders.list.month')),
      accessorKey: 'requestedForMonth',
      cell: Cell(cellType.Date),
      enableSorting: true,
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'avrechs',
      header: Col(t('kollels.folders.list.avrechs')),
      accessorFn: (row) => row.items.length,
      meta: { className: 'min-w-[100px]' },
    },
    {
      id: 'total',
      header: Col(t('kollels.folders.list.total')),
      cell: ({ row }) => <span>{formatCurrencyShort(folderTotal(row.original))}</span>,
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'status',
      header: Col(t('kollels.folders.list.status')),
      accessorKey: 'status',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div
            className="w-[10px] h-[10px] rounded-full shrink-0"
            style={{ backgroundColor: getKollelFolderStatusColor(row.original.status) }}
          />
          <span>{t(getKollelFolderStatusLabelKey(row.original.status))}</span>
        </div>
      ),
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'actions',
      header: Col(''),
      cell: ({ row }) =>
        row.original.status === KollelFolderStatus.PendingApproval ? (
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              setFolderToCancel(row.original);
            }}
          >
            {t('kollels.folders.list.cancel')}
          </Button>
        ) : null,
      meta: { className: 'min-w-[120px]' },
    },
  ];

  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => !isOpen && onClose()} title={t('kollels.folders.dialogTitle', { name: kollel.name })}>
      <div className="p-4">
        <DataTable
          title={t('kollels.folders.list.titleWithCount', { count: folders?.length ?? 0 })}
          data={folders ?? []}
          isLoading={isLoading}
          columns={columns}
          filters={
            kollel.status === KollelStatus.Approved ? (
              <Button variant="primary" onClick={() => setIsNewFolderOpen(true)}>
                {t('kollels.folders.list.add')}
              </Button>
            ) : (
              <p className="body-sm-regular text-(--font-secondary)">{t('kollels.folders.list.notApprovedHint')}</p>
            )
          }
          emptyStateContent={{ title: t('kollels.folders.list.noData'), description: t('kollels.folders.list.noDataDescription') }}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          customGlobalSearch={folderSearch}
        />
      </div>

      <NewKollelFolderDialog
        fundId={fundId}
        kollel={kollel}
        open={isNewFolderOpen}
        onClose={() => setIsNewFolderOpen(false)}
        onSuccess={refetch}
      />

      <ConfirmationDialog
        open={!!folderToCancel}
        onOpenChange={(isOpen) => !isOpen && setFolderToCancel(null)}
        title={t('kollels.folders.list.cancelConfirmTitle')}
        message={t('kollels.folders.list.cancelConfirmMessage')}
        confirmText="kollels.folders.list.cancel"
        cancelText="kollels.form.cancel"
        onConfirm={handleCancel}
        onCancel={() => setFolderToCancel(null)}
        isLoading={cancelMutation.isPending}
        variant="danger"
      />
    </MainDialogLayout>
  );
};

export default KollelFoldersDialog;
